import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { useCreateProjectPersonnelBulkCreationMutation } from 'components/hooks/projectPersonnelBulkCreationMutations';
import { useCreateProjectPersonnelBulkDestructionMutation } from 'components/hooks/projectPersonnelBulkDestructionMutations';

import SidePanel from 'components/application/SidePanel';
import SuccessToast from 'components/application/SuccessToast';
import Tabs from 'components/application/Tabs';
import UndoableToast from 'components/application/UndoableToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import ConfirmationModal from 'components/projectPersonnel/components/assignPersonnelSidePanel/components/ConfirmationModal';
import PersonnelTab from 'components/projectPersonnel/components/assignPersonnelSidePanel/components/PersonnelTab';
import RolesTab from 'components/projectPersonnel/components/assignPersonnelSidePanel/components/RolesTab';
import TeamsTab from 'components/projectPersonnel/components/assignPersonnelSidePanel/components/TeamsTab';
import useSelections from 'components/projectPersonnel/components/assignPersonnelSidePanel/hooks/useSelections';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';
import FormFooter from 'components/sidepanels/FormFooter';

import { personDisplayName } from 'components/helpers/users';

const DEFAULT_ACTIVE_TAB = 'personnel';

export default function AssignPersonnelSidePanel({ isOpen, onClose }) {
  const { addInedibleToast, addToast, handleBurnToast } = useBreadBoard();

  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { companyRolePolicy, projectId, teamPolicy } =
    useProjectPersonnelContext();

  const queryClient = useQueryClient();

  const createProjectPersonnelBulkCreationMutation =
    useCreateProjectPersonnelBulkCreationMutation({ projectId });

  const createProjectPersonnelBulkDestructionMutation =
    useCreateProjectPersonnelBulkDestructionMutation({ projectId });

  const {
    handlePersonnelCheckboxChange,
    handleProjectPersonnelTaken,
    handleRoleCheckboxChange,
    handleSelectionsReset,
    handleTeamCheckboxChange,
    isComputingPersonnelSelections,
    isComputingRoleSelections,
    isComputingTeamSelections,
    selections,
  } = useSelections();

  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectPersonnel.components.AssignPersonnelSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleConfirmationModalClose = () => {
    createProjectPersonnelBulkCreationMutation.reset();
    setIsConfirmationModalOpen(false);
  };

  const handleClose = () => {
    handleConfirmationModalClose();
    handleSelectionsReset();
    setActiveTab(DEFAULT_ACTIVE_TAB);
    onClose();
  };

  const handleUndoClick = (projectPersonnelIds) => {
    createProjectPersonnelBulkDestructionMutation.mutate(
      { projectPersonnelIds },
      {
        onSuccess: () => {
          addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='projectPersonnel.shared.createProjectPersonnelBulkDestruction.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: projectPersonnelIds.length,
                    singularRAMSName,
                  }}
                />
              }
              onBurnToast={handleBurnToast}
            />,
          );
        },
        onError: addInedibleToast,
      },
    );
  };

  const handleSubmitClick = (projectPersonnelAttributes) => {
    createProjectPersonnelBulkCreationMutation.mutate(
      { params: { project_personnel_attributes: projectPersonnelAttributes } },
      {
        onSuccess: (data) => {
          addToast(
            <UndoableToast
              onBurnToast={handleBurnToast}
              onUndoAction={() => {
                handleUndoClick(
                  data.data.relationships.projectPersonnel.data.map(
                    (projectPersonnel) => projectPersonnel.id,
                  ),
                );
              }}
              toastText={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='projectPersonnel.components.AssignPersonnelSidePanel.handleSubmitClick.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: projectPersonnelAttributes.length,
                    singularRAMSName,
                  }}
                />
              }
            />,
          );

          handleClose();
        },
        onError: (error) => {
          if (
            error.response.status === 422 &&
            _.isEqual(error.response.data.errors[0].types, ['taken'])
          ) {
            handleProjectPersonnelTaken({
              personnelIds: error.response.data.meta.personnelIds,
            });

            ['companyRoleSearches', 'teamSearches'].forEach((queryKeySegment) =>
              queryClient.removeQueries({
                queryKey: ['projects', projectId, queryKeySegment],
                predicate: ({ queryKey }) => queryKey.at(-1)?.page === 'all',
              }),
            );

            [
              'assignablePersonnelSearches',
              'companyRoleSearches',
              'projectPersonnel',
              'signatureStatus',
              'teamSearches',
            ].forEach((queryKeySegment) =>
              queryClient.invalidateQueries([
                'projects',
                projectId,
                queryKeySegment,
              ]),
            );
          } else {
            addInedibleToast();
          }
        },
      },
    );
  };

  const {
    personnel: selectedPersonnel,
    roleIds: selectedRoleIds,
    teamIds: selectedTeamIds,
  } = selections;

  const isSubmitDisabled =
    selectedPersonnel.size === 0 || isComputingPersonnelSelections;

  return (
    <>
      <SidePanel
        bodyContent={
          isOpen && (
            <Tabs
              activeTab={activeTab}
              modifiers={['panelled']}
              navClassName='tw-bg-grey-050'
              onTabClick={setActiveTab}
            >
              <PersonnelTab
                disabled={
                  activeTab !== 'personnel' && isComputingPersonnelSelections
                }
                id='personnel'
                label={i18nTCommon('shared.personnel')}
                onCheckboxChange={handlePersonnelCheckboxChange}
                selections={selections}
              />
              {teamPolicy.canIndex && (
                <TeamsTab
                  disabled={activeTab !== 'teams' && isComputingTeamSelections}
                  id='teams'
                  isComputingSelections={isComputingTeamSelections}
                  label={i18nTCommon('shared.teams')}
                  onCheckboxChange={handleTeamCheckboxChange}
                  selectedTeamIds={selectedTeamIds}
                />
              )}
              {companyRolePolicy.canIndex && (
                <RolesTab
                  disabled={activeTab !== 'roles' && isComputingRoleSelections}
                  id='roles'
                  isComputingSelections={isComputingRoleSelections}
                  label={i18nTCommon('shared.roles')}
                  onCheckboxChange={handleRoleCheckboxChange}
                  selectedRoleIds={selectedRoleIds}
                />
              )}
            </Tabs>
          )
        }
        closeCallback={handleClose}
        color='tertiary'
        footerContent={
          <FormFooter
            onCancel={handleClose}
            onSubmit={() => setIsConfirmationModalOpen(true)}
            submitButtonDisabled={isSubmitDisabled}
            text={
              isSubmitDisabled ?
                i18nTCommon('personnel.assignPersonnel')
              : i18nTCommon('personnel.assignCountPersonnel', {
                  count: selectedPersonnel.size,
                })
            }
          />
        }
        headerContent={
          <h1 className='tw-text-gray-900 tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight'>
            {i18nTCommon('personnel.addOrAssignPersonnel')}
          </h1>
        }
        isOpen={isOpen}
      />
      {isConfirmationModalOpen &&
        (() => {
          let confirmationModalErrorMessage;

          if (
            createProjectPersonnelBulkCreationMutation.isError &&
            createProjectPersonnelBulkCreationMutation.error.response.status ===
              422 &&
            _.isEqual(
              createProjectPersonnelBulkCreationMutation.error.response.data
                .errors[0].types,
              ['taken'],
            )
          ) {
            const { isAllTaken, limitedPersonnel, personnelIds } =
              createProjectPersonnelBulkCreationMutation.error.response.data
                .meta;

            if (isAllTaken) {
              confirmationModalErrorMessage = i18nT(
                'confirmationModalErrorMessage.allTaken',
                {
                  interpolation: { escapeValue: false },
                  singularRAMSName,
                },
              );
            } else {
              const name = personDisplayName({
                attributes: limitedPersonnel.data.attributes,
              });

              const remainingCount = personnelIds.length - 1;

              confirmationModalErrorMessage =
                remainingCount > 0 ?
                  i18nT('confirmationModalErrorMessage.takenWithCount', {
                    count: remainingCount,
                    interpolation: { escapeValue: false },
                    name,
                    singularRAMSName,
                  })
                : i18nT('confirmationModalErrorMessage.taken', {
                    interpolation: { escapeValue: false },
                    name,
                    singularRAMSName,
                  });
            }
          }

          return (
            <ConfirmationModal
              errorMessage={confirmationModalErrorMessage}
              isSubmitting={
                createProjectPersonnelBulkCreationMutation.isLoading
              }
              onClose={handleConfirmationModalClose}
              onSubmitClick={handleSubmitClick}
              personnelIds={new Set(selectedPersonnel.keys())}
            />
          );
        })()}
    </>
  );
}

AssignPersonnelSidePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
